import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TextInput, useModal } from '@jutro/components';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { appendMetadataWithIndex } from '@xengage/gw-jutro-adapters-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { AddressLookupService } from 'gw-capability-address';
import metadata from './AddressComponent.metadata.json5';
import { TranslatorContext } from '@jutro/legacy/locale';

function handleWarning(title, message, showAlertFunction) {
    return showAlertFunction({
        title,
        message,
        status: 'warning',
        icon: 'mi-error-outline',
        confirmButtonText: commonMessages.ok
    }).catch(_.noop);
}

function AddressComponent(props) {
    const {
        showAlert
    } = useModal();
    const {
        value: addressVM,
        labelPosition,
        showRequired,
        requiredFields,
        path,
        id,
        onValidate,
        onValueChange,
        showErrors,
        hideFields,
        index,
        readOnly,
        disabledFields,
        isNonSpecificLocation,
        callAddressValidateService
    } = props;
    const { isComponentValid, onValidate: setComponentValidation } = useValidation(id);
    const { authHeader } = useAuthentication();
    const formattedMetadata = useMemo(() => appendMetadataWithIndex(metadata.componentContent, index), [index]);
    const translator = useContext(TranslatorContext);

    const handleValueChange = useCallback(
        (value, changedPath) => {
            const fullPath = `${path}.${changedPath}`;
            

            if (onValueChange) {
                onValueChange(value, fullPath);
            }
        },
        [onValueChange, path]
    ); 
    
    // eslint-disable-next-line consistent-return
    const addressBlur = useCallback(async () => {
        if (addressVM.addressLine1.value !== undefined && addressVM.postalCode.value !== undefined
            && addressVM.addressLine1.value !== null && addressVM.postalCode.value !== null
            && addressVM.addressLine1.value !== '' && addressVM.postalCode.value !== '') {

           let error = '';
           const shouldRequestGeoLocation = true;

           addressVM.value.isValidationRequired = false
       
           if(callAddressValidateService){
               try {

                   const addressDTO = await AddressLookupService.validateAddress(addressVM.value, shouldRequestGeoLocation, authHeader);
       
                   error = addressDTO.validationErrorMsg_Ext;
       
                    _.set(addressVM, 'addressLine1', addressDTO.addressLine1);
                    _.set(addressVM, 'addressLine2', addressDTO.addressLine2);
                    _.set(addressVM, 'addressLine3', addressDTO.addressLine3);
                    _.set(addressVM, 'city', addressDTO.city);
                    _.set(addressVM, 'state', addressDTO.state);
                    _.set(addressVM, 'postalCode', addressDTO.postalCode);
                    _.set(addressVM, 'validationStatus_Ext', addressDTO.validationStatus_Ext);
                    _.set(addressVM, 'isValidationRequired', true);
                    handleValueChange(addressDTO.validationStatus_Ext, 'validationStatus_Ext')
               } catch (e) {
                    return handleWarning(
                       commonMessages.genericError,
                       error, 
                       showAlert
                   ); 
               }
           }
           

        }
    },
    [addressVM, authHeader, callAddressValidateService, handleValueChange, showAlert]
    );

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
        
    }, [id, onValidate, isComponentValid]);

    const overrideProps = {
        '@field': {
            labelPosition,
            showRequired,
            showErrors,
            disabled: disabledFields
        },
        [`addressTypeField${index}`]: {
            required: _.includes(requiredFields, 'addressType'),
            visible: !_.includes(hideFields, 'addressType'),
            readOnly: _.includes(readOnly, 'addressType')
        },
        [`addressLine1Field${index}`]: {
            required: _.isUndefined(isNonSpecificLocation) || !isNonSpecificLocation,
            showRequired: _.isUndefined(isNonSpecificLocation) || !isNonSpecificLocation,
            visible: !_.includes(hideFields, 'addressLine1Field'),
            readOnly: _.includes(readOnly, 'addressLine1Field')
        },
        [`addressLine2Field${index}`]: {
            visible: !_.includes(hideFields, 'addressLine2Field'),
            readOnly: _.includes(readOnly, 'addressLine2Field')
        },
        [`addressLine3Field${index}`]: {
            visible: !_.includes(hideFields, 'addressLine3Field'),
            readOnly: _.includes(readOnly, 'addressLine3Field')
        },
        [`addressCity${index}`]: {
            showRequired: _.isUndefined(isNonSpecificLocation) || !isNonSpecificLocation,
            visible: !_.includes(hideFields, 'addressCity'),
            readOnly: _.includes(readOnly, 'addressCity')
        },
        [`addressCounty${index}`]: {
            visible: !_.includes(hideFields, 'addressCounty'),
            readOnly: _.includes(readOnly, 'addressCounty')
        },
        [`addressState${index}`]: {
            visible: !_.includes(hideFields, 'addressState'),
            readOnly: _.includes(readOnly, 'addressState')
        },
        [`addressPostalCode${index}`]: {
            visible: !_.includes(hideFields, 'addressPostalCode'),
            readOnly: _.includes(readOnly, 'addressPostalCode')
        },
        [`addressCountry${index}`]: {
            visible: !_.includes(hideFields, 'addressCountry')
        },
        [`validationStatus_Ext${index}`]: {
            visible: !_.includes(hideFields, 'validationStatus_Ext'),
            value: _.get(addressVM.value, 'validationStatus_Ext') !== undefined && _.get(addressVM, 'validationStatus_Ext') !== undefined ?
                translator({
                    id: addressVM?.validationStatus_Ext?.aspects?.availableValues?.filter(value =>
                        value.code === _.get(addressVM.value, 'validationStatus_Ext'))[0]?.name,
                    defaultMessage: addressVM?.validationStatus_Ext?.aspects?.availableValues?.filter(value =>
                        value.code === _.get(addressVM.value, 'validationStatus_Ext'))[0]?.name
                }) : '-'
        },
    };

    const resolvers = {
        resolveCallbackMap: {
             addressBlur
        },
        resolveComponentMap: {
            TextInput
        }
    };


    return (
        <ViewModelForm
            uiProps={formattedMetadata}
            model={addressVM}
            overrideProps={overrideProps}
            onValidationChange={setComponentValidation}
            onValueChange={handleValueChange}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

AddressComponent.propTypes = {
    value: PropTypes.shape({}),
    labelPosition: PropTypes.string.isRequired,
    onValidate: PropTypes.func.isRequired,
    path: PropTypes.string,
    onValueChange: PropTypes.func.isRequired,
    id: PropTypes.string,
    requiredFields: PropTypes.arrayOf(PropTypes.string),
    hideFields: PropTypes.arrayOf(PropTypes.string),
    readOnly: PropTypes.arrayOf(PropTypes.string),
    showOptional: PropTypes.bool,
    showRequired: PropTypes.bool,
    showErrors: PropTypes.bool,
    viewOnlyMode: PropTypes.bool,
    index: PropTypes.number,
    disabledFields: PropTypes.bool,
    callAddressValidateService: PropTypes.bool
};
AddressComponent.defaultProps = {
    value: {},
    labelPosition: 'left', // labels on left by default
    requiredFields: [],
    hideFields: [],
    readOnly: [],
    id: undefined,
    showOptional: false,
    showRequired: true,
    showErrors: false,
    viewOnlyMode: false,
    index: 0,
    disabledFields: false,
    callAddressValidateService: true
};
export default AddressComponent;